import Link from "next/link";
import { useFormContext } from "react-hook-form";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";
import { InputError } from "src/components/base/components/FormComponents/FormComponents";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Los Angeles Angels Upgrades",
    description:
      "Enhance your game-day experience by upgrading your Los Angeles Angels tickets here. Upgrades are non-refundable.",
    authRedirectPage: routes.home,
    buttonLabel: "View Upgrades",
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How come I don't see seat upgrades available for purchase?",
    answer:
      "Upgrades are only eligible for those with a game ticket already. If you do not have a ticket for the game, please go to Angels Ticket Information to purchase.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability, please check back in on the day of the game to see eligible offers.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, seat upgrades will be delivered within your Ballpark App.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections noted in your seat upgrade offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please head to Ticket Resolution for any assistance you may require.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from angels.com . You may unsubscribe at any time. By checking this
    box you agree to receive additional commercial emails from angels.com and
    MLB.com. The terms and conditions of your ticket and policies applicable to
    its use are updated to address circumstances related to COVID-19 and health
    and safety. By clicking the Checkout button, you agree to accept all Terms
    and Conditions. Further, due to the COVID-19 pandemic, scheduling may be
    impacted; games may be canceled, rescheduled, or postponed or games may be
    held with limited capacity. Upon completing your purchase by selecting
    Checkout you acknowledge and agree that your credit card will be charged. By
    clicking Checkout, you agree to the{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      target="_blank"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      target="_blank"
    >
      Terms of Use
    </Link>{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Angels account. Message and data rates may apply.
    Tickets will be made available in the MLB Ballpark mobile application only.
    MLB.com and supported mobile device required.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 2,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/angels/tickets/ticketback",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        General Ticketing Terms & Conditions | Los Angeles Angels
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export function CheckoutTermsCheckbox() {
  const theme = useSSTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const primaryFieldName = "terms";
  const secondaryFieldName = "secondaryTerms";
  const errorMessage = errors[primaryFieldName]?.message as string;
  const secondaryErrorMessage = errors[secondaryFieldName]?.message as string;

  return (
    <Box>
      <Box>
        <FormControlLabel
          sx={{
            color: theme.colors.gray[200],
            alignItems: "flex-start",
            py: 0.5,
            transition: "all 0.3s ease",
            margin: 0,

            "&:hover": {
              backgroundColor: theme.colors.gray[50],
              borderRadius: "4px",
            },
          }}
          label={
            <SSText variant="caption" fontSize={"0.9rem"}>
              By clicking Purchase Pass below, you agree to the{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
              >
                MLB Terms of Use
              </Link>{" "}
              and{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
              >
                Privacy policy
              </Link>
              , as well as Angels Baseball{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/angels/tickets/ticketback"
              >
                Terms & Conditions
              </Link>
              .
            </SSText>
          }
          control={
            <Checkbox
              size="medium"
              disableRipple
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "1.8rem",
                  marginTop: "-8px",
                  marginLeft: "-6px",
                },
              }}
              {...register("terms", {
                required: "Please accept the terms and conditions",
              })}
            />
          }
        />
        {errorMessage ? <InputError>{errorMessage}</InputError> : null}
      </Box>

      <Box>
        <FormControlLabel
          sx={{
            color: theme.colors.gray[200],
            alignItems: "flex-start",
            py: 0.5,
            transition: "all 0.3s ease",
            margin: 0,

            "&:hover": {
              backgroundColor: theme.colors.gray[50],
              borderRadius: "4px",
            },
          }}
          label={
            <SSText variant="caption" fontSize={"0.9rem"}>
              By checking this box, I agree to receive automated texts from
              Angels Baseball LP, including promotional, informational, and/or
              transactional information. Message and data rates may apply. By
              agreeing to automated text messages, you agree to these{" "}
              <Link
                target="_blank"
                href="https://www.mlb.com/app/ballpark/angels/tickets/ticketback"
              >
                Terms & Conditions
              </Link>
              . Message frequency varies. You may opt-out at any time.
            </SSText>
          }
          control={
            <Checkbox
              size="medium"
              disableRipple
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "1.8rem",
                  marginTop: "-8px",
                  marginLeft: "-6px",
                },
              }}
              {...register(secondaryFieldName, {
                required: "Please accept the terms and conditions",
              })}
            />
          }
        />
        {secondaryErrorMessage ? (
          <InputError>{secondaryErrorMessage}</InputError>
        ) : null}
      </Box>
    </Box>
  );
}
